import { FC, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { UploadProps } from 'antd';

import { useFiles } from '@/entities/file';
import { useFileEncryptionUpload } from '@/entities/file/hooks/useFileEncryptionUpload';
import { useNotices } from '@/features/notices/useNotices';
import { files as fileHelpers } from '@/shared/helpers';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { useProfileStore } from '@/shared/store/profile.store';
import { Title } from '@/shared/uikit/Title';
import { Spinner } from '@/shared/uikitV2/Spinner';
import {
  FileUploader,
  FileUploaderOptions,
  FileUploaderRef,
  UploadState,
} from '@/widgets/FileUploader';
import { useModalsStore } from '@/widgets/modals';
import { FileItem } from '@/widgets/modals/containers/UploadFiles/FileItem';
import { FileItemInfo } from '@/widgets/modals/containers/UploadFiles/types';

import { AssetPhotoUploader } from './AssetPhotoUploader';
import { FileUploadingContainer } from './styled';

export type FileUploadingRef = {
  updateFilesChanges?: () => Promise<string[]>;
} & FileUploaderRef;

type FileUploadingProps = {
  name: string;
  label: string;
  uploaderRef?: RefObject<FileUploadingRef>;
  value?: Array<string>;
  setValue: UseFormSetValue<Record<string, unknown>>;
} & UploadProps &
  FileUploaderOptions;

export const FileUploading: FC<FileUploadingProps> = ({
  label,
  uploaderRef,
  value: filePaths = [],
  acceptFiles,
  allowedActions,
}) => {
  const [uploadState, setUploadState] = useState<UploadState | null>();
  const { decryptDocumentFile } = useFiles();
  const sjDb = useSJDatabase();
  const [filesView, setFilesView] = useState<FileItemInfo[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const modals = useModalsStore();
  const childRef = useRef<FileUploaderRef>(null);
  const notices = useNotices();
  const { isAssistant } = useProfileStore();

  const { fullyDeleteUploadedFile } = useFileEncryptionUpload({
    useDirectUrl: true,
  });

  const filesMetaInfosByPaths: FileMetaInfoDto[] =
    sjDb.collections.fileMetaInfos?.findMany((item) =>
      Boolean(filePaths.includes(item.filePath)),
    ) ?? [];

  const actualFilesMetaInfos = filesMetaInfosByPaths.filter((fileMetaInfo) =>
    filesView.some((fileView) => fileView.id === fileMetaInfo.id),
  );

  const updateFilesChanges = async () => {
    updateUploadedFiles();
    await deleteUploadedFiles();

    return filePaths.filter((path) =>
      actualFilesMetaInfos.some((actualFileMetaInfo) => actualFileMetaInfo.filePath === path),
    );
  };

  const updateUploadedFiles = () => {
    const filesToUpdate: FileMetaInfoDto[] = [];

    for (const actualFile of filesView) {
      const file = actualFilesMetaInfos.find((item) => item.id === actualFile.id);
      if (file) filesToUpdate.push({ ...file, comment: actualFile.comment });
    }

    sjDb.collections.fileMetaInfos?.updateMany(filesToUpdate);
  };

  const deleteUploadedFiles = async () => {
    const metaInfosToRemove = filesMetaInfosByPaths.filter(
      ({ id }) => !actualFilesMetaInfos.some((actual) => actual.id === id),
    );

    return await Promise.all(
      metaInfosToRemove.map(({ filePath }) => fullyDeleteUploadedFile({ filePath })),
    );
  };

  const openEditComment = (file: FileItemInfo, inx: number) => {
    modals.openModal({
      type: 'EditComment',
      file: file.file,
      comment: file.comment,
      onChange: (comment) => {
        setFilesView((prev) => prev.map((item, i) => (i === inx ? { ...item, comment } : item)));
      },
    });
  };

  const fetchFiles = async () => {
    setFilesLoading(true);
    const promises = filesMetaInfosByPaths.map(
      (metaDto): Promise<FileItemInfo> =>
        new Promise((resolve, reject) => {
          decryptDocumentFile(metaDto.filePath, { name: metaDto.name })
            .then((file) => {
              if (!file) {
                reject(undefined);
                return;
              }

              resolve({
                file,
                id: metaDto.id,
                comment: metaDto.comment,
                isValidType: fileHelpers.isValidType('attachments', file.type),
                isValidSize: fileHelpers.isValidSize(file.size),
                uploaded: true,
              });
            })
            .catch(reject);
        }),
    );

    try {
      const files = await Promise.all(promises);
      setFilesView(files);
    } catch {
      notices.error('Something went wrong during attached file(s) loading');
    }

    setFilesLoading(false);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    // onChangeState(Boolean(uploadState?.uploading));
  }, [uploadState?.uploading]);

  useImperativeHandle(
    uploaderRef,
    (): FileUploadingRef => ({
      updateFilesChanges,
      addFiles: (files) => childRef.current?.addFiles?.(files),
      upload: (isAttached) => {
        return (
          childRef.current?.upload(isAttached) ?? Promise.resolve({ success: false, filePaths: [] })
        );
      },
    }),
    [filesView],
  );

  if (acceptFiles === 'assetPhotos' && childRef) {
    return (
      <FileUploadingContainer>
        <AssetPhotoUploader
          onRemove={(removeId) => {
            if (isAssistant) return;
            setFilesView((prev) => prev.filter(({ id }) => id !== removeId));
          }}
          isLoading={filesLoading}
          uploaderRef={childRef}
          files={filesView}
        />
      </FileUploadingContainer>
    );
  }

  return (
    <FileUploadingContainer>
      <Title bold size={16}>
        {label}
      </Title>
      <FileUploader
        acceptFiles={acceptFiles}
        allowedActions={allowedActions}
        onUpdate={setUploadState}
        ref={childRef}
      />
      {filesLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      ) : (
        filesView.map((file, inx) => (
          <FileItem
            fileInfo={file}
            allowedActions={allowedActions}
            onRemove={() => {
              if (isAssistant) return;
              setFilesView((prev) => prev.filter(({ id }) => id !== file.id));
            }}
            onChangeComment={() => openEditComment(file, inx)}
            key={file.id}
          />
        ))
      )}
    </FileUploadingContainer>
  );
};
